import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  getDomainByIdQuery,
  getDomainListByUserIdQuery,
  createDomainQuery,
  updateDomainQuery,
  deleteDomainQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";
import { canUserPerformAction } from "access-roles/general-functions";
import { ROLE_GET_DOMAIN } from "access-roles/roles";

const DomainContext = createContext({});

export const useDomainContext = () => useContext(DomainContext);

export const DomainProvider = ({ ...props }) => {
  const [domainList, setDomainList] = useState([]);
  const [domainById, setDomainById] = useState({});
  const [refresh, setRefresh] = useState(false);
  const { userScope, userRoleList } = useUserContext();

  const getDomainById = useCallback(
    async ({ id }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: ROLE_GET_DOMAIN,
        })
      ) {
        return;
      }
      try {
        setDomainById(await getDomainByIdQuery({ id, scope: userScope }));
      } catch (e) {
        console.error("getDomainByIdQ", e);
        throw e;
      }
    },
    [domainById, userScope, userRoleList]
  );

  const getDomainListByUserId = useCallback(
    async ({ id }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: ROLE_GET_DOMAIN,
        })
      ) {
        return;
      }
      try {
        setDomainList(await getDomainListByUserIdQuery({ id, userScope }));
      } catch (e) {
        console.error("getDomainListByUserId", e);
        throw e;
      }
    },
    [domainList, userScope, userRoleList]
  );

  const createDomain = useCallback(async (data) => {
    try {
      await createDomainQuery(data);
    } catch (e) {
      console.error("createDomain", e);
      throw e;
    }
  }, []);

  const updateDomain = useCallback(async ({ id, data }) => {
    try {
      await updateDomainQuery({ id, data });
      setRefresh(true);
    } catch (e) {
      console.error("updateDomain", e);
      throw e;
    }
  }, []);

  const deleteDomain = useCallback(async (id) => {
    try {
      await deleteDomainQuery(id);
    } catch (e) {
      console.error("deleteDomain", e);
      throw e;
    }
  }, []);

  const values = useMemo(
    () => ({
      userScope,
      getDomainById,
      getDomainListByUserId,
      createDomain,
      updateDomain,
      deleteDomain,
      domainList,
      domainById,
      refresh,
      setRefresh,
    }),
    [domainList, domainById, refresh, setRefresh, userScope]
  );

  return <DomainContext.Provider value={values} {...props} />;
};
